/**
 * `TeamDataSourcePicker` — which dataset feeds this team, chosen in the
 * team's own header (shipcast-o7j Q1).
 *
 * The choice belongs to the team, not to either section that reads it: the
 * Deliverables and Throughput setup windows both follow it. So it sits next
 * to the team's name — the first thing someone asking "where does this team's
 * data come from?" looks at — and nowhere else.
 *
 * Picking does not fetch or apply anything: each section refreshes its own
 * part when asked (shipcast-o7j.3). Datasets themselves are defined by admins
 * on the Datasets page; here a team only chooses among them, or none.
 *
 * It also says, at pick time, when a sibling team already reads the same
 * dataset: both would import the same deliverables with the same counts, so
 * the forecast could not tell their work apart. The ingest raises that too,
 * but only after a refresh, and its symptom — identical numbers — is silent.
 * A warning, not a block: sharing can be deliberate.
 */

import { useId } from 'react';
import { AlertTriangle, Database } from 'lucide-react';

import {
  useDatasets,
  useSetTeamDataset,
  useTeamTrackerState,
  useTeamTrackerStates,
  useTrackerCapabilities,
} from '../hooks';

/** Another team of the same simulation, as the form knows it. */
export interface SiblingTeam {
  id: string;
  name: string;
}

export interface TeamDataSourcePickerProps {
  /** The team's server id; `undefined` until the team is first saved. */
  teamId: string | undefined;
  /** The simulation's other SAVED teams — a pre-save team reads no dataset. */
  siblings?: readonly SiblingTeam[];
}

export function TeamDataSourcePicker({ teamId, siblings = [] }: TeamDataSourcePickerProps) {
  const { data: capabilities } = useTrackerCapabilities();
  // Unlicensed or unconfigured: there is nothing to pick from, and the
  // Features section already says why.
  if (!capabilities?.licensed || !capabilities.configured) return null;
  if (!teamId) {
    return <p className="mt-0.5 text-xs text-text-muted">Save the simulation to pick a data source for this team.</p>;
  }
  return <Picker teamId={teamId} siblings={siblings} />;
}

function Picker({ teamId, siblings }: { teamId: string; siblings: readonly SiblingTeam[] }) {
  const selectId = useId();
  const { data: state, isLoading } = useTeamTrackerState(teamId);
  const { data: datasets } = useDatasets();
  const setDataset = useSetTeamDataset();
  const siblingStates = useTeamTrackerStates(siblings.map((s) => s.id));

  const datasetId = state?.dataset_id ?? null;
  // A sibling still loading is not a sharer yet: the warning appears when it
  // is known, never guessed.
  const sharedWith = datasetId ? siblings.filter((_s, i) => siblingStates[i]?.data?.dataset_id === datasetId) : [];

  return (
    <div className="mt-1 space-y-1">
      <div className="flex items-center gap-1.5 text-xs text-text-muted">
        <Database className="size-3.5 shrink-0" aria-hidden="true" />
        <label htmlFor={selectId}>Data source</label>
        <select
          id={selectId}
          value={datasetId ?? ''}
          onChange={(e) => setDataset.mutate({ simulationTeamId: teamId, datasetId: e.target.value || null })}
          disabled={isLoading || setDataset.isPending}
          className="
            min-w-0 rounded-sm border border-border-default bg-transparent px-1.5 py-0.5 text-xs text-text-primary
            disabled:opacity-60
          "
        >
          <option value="">None — entered by hand</option>
          {datasets?.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>
      {setDataset.error instanceof Error && (
        <p role="alert" className="text-xs text-feedback-error">
          Could not change the data source: {setDataset.error.message}
        </p>
      )}
      {sharedWith.length > 0 && (
        <p role="alert" className="flex items-start gap-1.5 text-xs text-feedback-warning">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>
            Also used by {joinNames(sharedWith.map((s) => s.name))} in this simulation. Both teams would import the same
            deliverables with the same counts, so the forecast could not tell their work apart — give each team a
            dataset whose query selects only its own items.
          </span>
        </p>
      )}
    </div>
  );
}

/** "A", "A and B", "A, B and C". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
